import {
	AdminPanelSettingsOutlined as AdminPanelSettingsIcon,
	AssignmentOutlined as AssignmentIcon,
	BusinessOutlined as BusinessIcon,
	GroupsOutlined as GroupsIcon,
	HistoryOutlined as HistoryIcon,
	AssignmentIndOutlined as AssignmentIndOutlinedIcon,
	PersonSearchOutlined as PersonSearchIcon,
	AccountCircleOutlined as AccountCircleIcon,
	CampaignOutlined as CampaignIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { UserInformationViewModel } from "../../ViewModels/UserInformationViewModel";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { User } from "../../models/User";
import { UserRole } from "../../models/UserRole";
import { useProjectFilter } from "../../contexts/ProjectFilterContext";
import { UserType } from "../../models/UserType";
import Menu, { MenuItem } from "./Menu";
import { useInternalUserContext } from "../../contexts/useInternalUserContext";
import { useProjectStatuses } from "../../hooks/useProject";
import { generateProjectStatusMenuItems } from "../../utils/menuHelpers";

interface Props {
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	drawerWidth: number;
	user: User;
}

const InternalMenu: React.FC<Props> = ({
	isCollapsed,
	setIsCollapsed,
	drawerWidth,
	user
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		selectedFreelancerOnboardingStatus,
		setSelectedFreelancerOnboardingStatus,
		isUIDisabled
	} = useInternalUserContext();
	const { statusFilter: currentStatusFilter } = useProjectFilter();
	const { data: projectStatuses } = useProjectStatuses();

	const getMenuItemFromRoute = (
		pathname: string,
		freelancerOnboardingStatus: FreelancerOnboardingStatus | "All",
		search: string
	) => {
		if (pathname.startsWith("/analytics")) {
			return "dashboard";
		} else if (pathname.startsWith("/users")) {
			return "userManagement";
		} else if (pathname.startsWith("/freelancer-export")) {
			return "freelancerExport";
		} else if (pathname.startsWith("/audit-logs")) {
			return "auditLogs";
		} else if (pathname.startsWith("/applications")) {
			return freelancerOnboardingStatus === "All"
				? "candidaturesGroup"
				: `applications-${freelancerOnboardingStatus}`;
		} else if (pathname === "/projects/create") {
			return "createProject";
		} else if (pathname.startsWith("/projects")) {
			const params = new URLSearchParams(search);
			const status = params.get("status");
			if (status) {
				return `${status.toLowerCase()}Projects`;
			}
			return "allProjectsGroup";
		} else if (pathname.startsWith("/community")) {
			return "communityDashboard";
		} else if (pathname.startsWith("/company-groups")) {
			return "companyGroups";
		} else if (pathname.startsWith("/companies")) {
			return "companies";
		} else if (pathname.startsWith("/clients")) {
			return "clients";
		} else if (pathname.startsWith("/talent-search")) {
			return "talentSearch";
		} else if (pathname.startsWith("/talent-prospection")) {
			return "newProspectSelection";
		} else if (pathname.startsWith("/talent-selections")) {
			return "talentSelections";
		} else if (pathname.startsWith("/evaluations")) {
			return "evaluations";
		} else if (pathname.startsWith("/internal-profile")) {
			return "internalProfile";
		}

		return "companies";
	};

	const getInitialActiveMenuItem = (
		freelancerOnboardingStatus: FreelancerOnboardingStatus | "All"
	) => {
		const routeBasedItem = getMenuItemFromRoute(
			location.pathname,
			freelancerOnboardingStatus,
			location.search
		);
		const storageKey = `zoulloo_internal_menu_active_item_user_${user.id}`;
		const storedItem = sessionStorage.getItem(storageKey);

		if (!storedItem) {
			sessionStorage.setItem(storageKey, routeBasedItem);
			return routeBasedItem;
		}

		return storedItem;
	};

	const [activeMenuItem, setActiveMenuItem] = useState(
		getInitialActiveMenuItem(selectedFreelancerOnboardingStatus)
	);

	const getPrimaryUserRole = () => {
		if (user.roles?.includes(UserRole.Admin)) {
			return t(`roles.admin`);
		} else if (user.roles?.includes(UserRole.TAM)) {
			return t(`roles.tam`);
		} else if (user.roles?.includes(UserRole.CSM)) {
			return t(`roles.csm`);
		}

		return t(`enums.userType.${UserType.Internal}`);
	};

	const [userInformationViewModel, setUserInformationViewModel] =
		useState<UserInformationViewModel>({
			userId: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			type: getPrimaryUserRole(),
			profilePhotoUrl: ""
		});

	useEffect(() => {
		setUserInformationViewModel({
			userId: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			type: getPrimaryUserRole(),
			profilePhotoUrl: ""
		});
	}, [user]);

	// Listen for route changes and update active menu item
	useEffect(() => {
		const newActiveItem = getMenuItemFromRoute(
			location.pathname,
			selectedFreelancerOnboardingStatus,
			location.search
		);

		if (newActiveItem !== activeMenuItem) {
			const storageKey = `zoulloo_internal_menu_active_item_user_${user.id}`;

			sessionStorage.setItem(storageKey, newActiveItem);

			setActiveMenuItem(newActiveItem);
		}
	}, [location.pathname, selectedFreelancerOnboardingStatus, location.search]);

	// Sync active menu item with current filter when on projects page
	useEffect(() => {
		if (
			location.pathname.startsWith("/projects") &&
			location.pathname !== "/projects/create"
		) {
			let newActiveItem = "allProjectsGroup";
			if (currentStatusFilter && currentStatusFilter !== "") {
				newActiveItem = `${currentStatusFilter.toLowerCase()}Projects`;
			}

			if (newActiveItem !== activeMenuItem) {
				setActiveMenuItem(newActiveItem);
			}
		}
	}, [currentStatusFilter, location.pathname]);

	const hasRole = (role: string) => user.roles?.includes(role);

	const handleMenuItemClick = (menuKey: string, navigationPath: string) => {
		const storageKey = `zoulloo_internal_menu_active_item_user_${user.id}`;

		sessionStorage.setItem(storageKey, menuKey);

		navigate(navigationPath);
	};

	const mainMenuItems: MenuItem[] = [];

	// === 1. Admin-only: User Management & Audit Logs ===
	if (hasRole(UserRole.Admin)) {
		mainMenuItems.push(
			{
				key: "userManagement",
				text: t("menu.userManagement"),
				icon: <AdminPanelSettingsIcon fontSize="medium" />,
				onClick: () => handleMenuItemClick("userManagement", "/users")
			},
			{
				key: "auditLogs",
				text: t("menu.auditLogs"),
				icon: <HistoryIcon fontSize="medium" />,
				onClick: () => handleMenuItemClick("auditLogs", "/audit-logs")
			}
		);
	}

	// === 2. Talent Acquisition (TAM + Admin) ===
	// Order: Candidatures (All + statuses), then Tableau de bord at the end
	if (hasRole(UserRole.TAM) || hasRole(UserRole.Admin)) {
		const applicationStatusSubmenus: MenuItem[] = Object.values(FreelancerOnboardingStatus)
			.filter((value): value is FreelancerOnboardingStatus => typeof value === "string")
			.map(status => {
				const statusText = t(`enums.FreelancerOnboardingStatus.${status}`);
				return {
					key: `applications-${status}`,
					text: statusText,
					icon: null,
					onClick: () => {
						handleMenuItemClick(`applications-${status}`, "/applications");
						setSelectedFreelancerOnboardingStatus(status);
					},
					disableClick: isUIDisabled
				} as MenuItem;
			});

		mainMenuItems.push({
			key: "talentAcquisition",
			text: t("menu.talentAcquisition"),
			icon: <AssignmentIndOutlinedIcon fontSize="medium" />,
			onClick: undefined,
			submenu: [
				{
					key: "candidaturesGroup",
					text: t("menu.candidatures"),
					icon: null,
					onClick: () => {
						handleMenuItemClick("applications-All", "/applications");
						setSelectedFreelancerOnboardingStatus("All");
					},
					submenu: applicationStatusSubmenus
				},
				{
					key: "dashboard",
					text: t("menu.dashboard"),
					icon: null,
					onClick: () => handleMenuItemClick("dashboard", "/analytics")
				}
			]
		});
	}

	// === 3. Projects (CSM + Admin) — includes Evaluations as submenu ===
	if (hasRole(UserRole.CSM) || hasRole(UserRole.Admin)) {
		const statusMenuItems = projectStatuses
			? generateProjectStatusMenuItems(projectStatuses, t, navigate, "/projects/all")
			: [];

		mainMenuItems.push({
			key: "projects",
			text: t("menu.projects"),
			icon: <AssignmentIcon fontSize="medium" />,
			onClick: undefined,
			submenu: [
				{
					key: "createProject",
					text: t("menu.createProject"),
					icon: null,
					activateClickOnActiveItem: true,
					onClick: () => {
						handleMenuItemClick("createProject", "/projects/create");
					}
				},
				{
					key: "allProjectsGroup",
					text: t("menu.allProjects"),
					icon: null,
					onClick: () => {
						handleMenuItemClick("allProjectsGroup", "/projects/all");
					},
					submenu: statusMenuItems.filter(item => item.key !== "allProjects")
				},
				{
					key: "evaluations",
					text: t("menu.evaluations"),
					icon: null,
					onClick: () => handleMenuItemClick("evaluations", "/evaluations")
				}
			]
		});
	}

	// === 5. Talents (all internal users) — internal recruitment / search ===
	if (hasRole(UserRole.Admin) || hasRole(UserRole.TAM) || hasRole(UserRole.CSM)) {
		const talentsSubmenus: MenuItem[] = [
			{
				key: "talentSearch",
				text: t("menu.talentSearch"),
				icon: null,
				onClick: () => handleMenuItemClick("talentSearch", "/talent-search")
			}
		];

		if (hasRole(UserRole.Admin)) {
			talentsSubmenus.push({
				key: "freelancerExport",
				text: t("menu.freelancerExport"),
				icon: null,
				onClick: () => handleMenuItemClick("freelancerExport", "/freelancer-export")
			});
		}

		talentsSubmenus.push({
			key: "communityDashboard",
			text: t("menu.communityDashboard"),
			icon: null,
			onClick: () => handleMenuItemClick("communityDashboard", "/community")
		});

		mainMenuItems.push({
			key: "talents",
			text: t("menu.talents"),
			icon: <PersonSearchIcon fontSize="medium" />,
			onClick: undefined,
			submenu: talentsSubmenus
		});
	}

	// === 5b. Prospection (Option B) — outbound prospecting via anonymized links ===
	// Kept as a SEPARATE top-level menu rather than nested under "Talents" so the
	// commercial workflow (build a selection → save → share public links → track
	// views) is immediately discoverable and doesn't get confused with the
	// recruitment-into-project flow.
	//
	// The daily client/talent matcher's audit (worker output verification) is
	// rendered inline on each client's detail page rather than as a dedicated
	// menu item, so there's no "audit" entry here.
	if (hasRole(UserRole.Admin) || hasRole(UserRole.TAM) || hasRole(UserRole.CSM)) {
		mainMenuItems.push({
			key: "prospection",
			text: t("anonymizedTalent.menuProspection"),
			icon: <CampaignIcon fontSize="medium" />,
			onClick: undefined,
			submenu: [
				{
					key: "newProspectSelection",
					text: t("anonymizedTalent.menuNewProspectSelection"),
					icon: null,
					onClick: () =>
						handleMenuItemClick("newProspectSelection", "/talent-prospection/new")
				},
				{
					key: "talentSelections",
					text: t("anonymizedTalent.menuTalentSelections"),
					icon: null,
					onClick: () =>
						handleMenuItemClick("talentSelections", "/talent-selections")
				}
			]
		});
	}

	// === 6. Entreprises (all internal users) ===
	if (hasRole(UserRole.Admin) || hasRole(UserRole.TAM) || hasRole(UserRole.CSM)) {
		mainMenuItems.push({
			key: "entreprises",
			text: t("menu.companies"),
			icon: <BusinessIcon fontSize="medium" />,
			onClick: undefined,
			submenu: [
				{
					key: "companies",
					text: t("menu.companies"),
					icon: null,
					onClick: () => handleMenuItemClick("companies", "/companies")
				},
				{
					key: "companyGroups",
					text: t("menu.companyGroups"),
					icon: null,
					onClick: () => handleMenuItemClick("companyGroups", "/company-groups")
				}
			]
		});
	}

	// === 7. Clients (CSM + Admin) ===
	if (hasRole(UserRole.Admin) || hasRole(UserRole.CSM)) {
		mainMenuItems.push({
			key: "clients",
			text: t("menu.clients"),
			icon: <GroupsIcon fontSize="medium" />,
			onClick: () => handleMenuItemClick("clients", "/clients")
		});
	}

	// === 8. Mon profil / Profils internes ===
	// Page to edit the InternalUser data (PhoneNumber, scheduling URLs).
	// - Admin: sees a user picker — can edit any internal user → label "Profils internes"
	// - CSM/TAM: only edits own profile → label "Mon profil"
	if (hasRole(UserRole.Admin) || hasRole(UserRole.TAM) || hasRole(UserRole.CSM)) {
		const profileMenuLabel = hasRole(UserRole.Admin)
			? t("menu.internalProfilesAdmin", "Profils internes")
			: t("menu.internalProfile", "Mon profil");

		mainMenuItems.push({
			key: "internalProfile",
			text: profileMenuLabel,
			icon: <AccountCircleIcon fontSize="medium" />,
			onClick: () => handleMenuItemClick("internalProfile", "/internal-profile")
		});
	}

	return (
		<Menu
			isCollapsed={isCollapsed}
			setIsCollapsed={setIsCollapsed}
			drawerWidth={drawerWidth}
			defaultActiveItem={activeMenuItem}
			userInformationViewModel={userInformationViewModel}
			lastActiveMenuStorageKey={`zoulloo_internal_menu_active_item_user_${user.id}`}
			mainMenuItems={mainMenuItems}
		/>
	);
};

export default InternalMenu;
